import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/types';

export function NavBar() {
  const { user, logout, impersonatingRole, setImpersonatingRole } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  }

  return (
    <header className="flex items-center justify-between px-5 h-12 bg-surface border-b border-border sticky top-0 z-[100]">
      {/* Logo & app name */}
      <div className="flex items-center gap-2.5">
        <img
          src="/baker-logo.jpg"
          alt="Baker Distributing"
          className="h-6 w-auto"
        />
        <span className="text-sm font-semibold text-text-primary tracking-tight">
          Nexus
        </span>
      </div>

      {/* Global search */}
      <form onSubmit={handleSearch} className="flex-shrink flex-grow-0 basis-[360px]">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder="Search vendors, contracts, invoices…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Global search"
            className="w-full py-1.5 pl-9 pr-3 rounded-lg bg-page border border-border text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
          />
        </div>
      </form>

      {/* User info & logout */}
      <div className="flex items-center gap-3 text-sm">
        {user?.isAdmin && (
          <select
            value={impersonatingRole ?? ''}
            onChange={(e) => setImpersonatingRole(e.target.value ? e.target.value as UserRole : null)}
            className="py-1 px-2 rounded border border-border text-xs bg-white"
            title="View as role"
          >
            <option value="">Admin view</option>
            {Object.values(UserRole).filter(r => r !== 'Admin').map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        )}
        {impersonatingRole && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
            Viewing as: {impersonatingRole}
          </span>
        )}
        {user && (
          <span className="text-text-secondary text-xs">{user.displayName}</span>
        )}
        <button
          onClick={logout}
          className="py-1 px-2.5 rounded-md border border-border bg-transparent text-text-secondary text-xs font-medium cursor-pointer hover:bg-page hover:text-text-primary transition-colors"
        >
          Logout
        </button>
      </div>
    </header>
  );
}
